import { useState, useEffect } from 'react';

const API_URL = import.meta.env.VITE_API_URL || '/api';

type WorkerClassification = 'w2_hourly' | 'w2_salary' | '1099_contractor' | '1099_commission' | '1099_mixed';

interface EmployeeTaxInfo {
  id: string;
  name: string;
  email: string;
  classification: WorkerClassification;
  ssn_last4: string; // Only last 4 digits stored in frontend
  ssn_masked: string; // ***-**-1234
  ein_or_ssn_type: 'ssn' | 'ein';
  address: {
    street: string;
    city: string;
    state: string;
    zip: string;
  };
  filing_status: 'single' | 'married' | 'head_of_household';
  ytd_gross: number;
  ytd_federal_tax: number;
  ytd_social_security: number;
  ytd_medicare: number;
  ytd_state_tax: number;
  ytd_local_tax: number;
  employer_ss: number;
  employer_medicare: number;
  ytd_nonemployee_comp?: number; // 1099-NEC Box 1
  ytd_commission?: number;
}

interface W2Data {
  year: number;
  employee: EmployeeTaxInfo;
  employer: {
    name: string;
    ein: string;
    address: string;
  };
  box1_wages: number;
  box2_federal_tax: number;
  box3_ss_wages: number;
  box4_ss_tax: number;
  box5_medicare_wages: number;
  box6_medicare_tax: number;
  box15_state: string;
  box16_state_wages: number;
  box17_state_tax: number;
}

export function PayrollReportsPage() {
  const [activeTab, setActiveTab] = useState<'w2' | '1099' | 'quarterly' | 'annual' | 'employee-tax'>('w2');
  const [employees, setEmployees] = useState<EmployeeTaxInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeTaxInfo | null>(null);
  const [showW2Modal, setShowW2Modal] = useState(false);
  const [showSSNModal, setShowSSNModal] = useState(false);
  const [ssnPassword, setSSNPassword] = useState('');
  const [ssnError, setSSNError] = useState('');
  const [revealedSSN, setRevealedSSN] = useState<string | null>(null);
  const [editingTaxInfo, setEditingTaxInfo] = useState<EmployeeTaxInfo | null>(null);
  const [newSSN, setNewSSN] = useState('');

  // Employer info (would come from company settings)
  const employerInfo = {
    name: 'Keder Screens / International Shade Products',
    ein: '**-***4567',
    address: 'Vernon, TX 76384',
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_auth_token');
      const headers = { Authorization: `Bearer ${token}` };
      const res = await fetch(`${API_URL}/payroll/employees/tax-info?year=${selectedYear}`, { headers });
      if (res.ok) {
        setEmployees(await res.json());
      } else {
        console.error('Tax-info API returned', res.status);
        setEmployees([]);
      }
    } catch (error) {
      console.error('Failed to load tax info:', error);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _generateW2 = (employee: EmployeeTaxInfo): W2Data => {
    return {
      year: selectedYear,
      employee,
      employer: employerInfo,
      box1_wages: employee.ytd_gross,
      box2_federal_tax: employee.ytd_federal_tax,
      box3_ss_wages: Math.min(employee.ytd_gross, 168600), // SS wage base
      box4_ss_tax: employee.ytd_social_security,
      box5_medicare_wages: employee.ytd_gross,
      box6_medicare_tax: employee.ytd_medicare,
      box15_state: 'TX',
      box16_state_wages: employee.ytd_gross,
      box17_state_tax: 0, // Texas has no state income tax
    };
  };

  const handleViewW2 = (employee: EmployeeTaxInfo) => {
    setSelectedEmployee(employee);
    setShowW2Modal(true);
  };

  const handleViewSSN = (employee: EmployeeTaxInfo) => {
    setSelectedEmployee(employee);
    setSSNPassword('');
    setSSNError('');
    setRevealedSSN(null);
    setShowSSNModal(true);
  };

  const handleVerifySSNPassword = async () => {
    // In production, this would verify against a secure endpoint
    // For demo, we use a fixed admin password
    const ADMIN_PASSWORD = 'Keder2026!'; // Should be verified server-side in production

    if (ssnPassword === ADMIN_PASSWORD) {
      // Fetch full SSN from secure endpoint
      try {
        const token = localStorage.getItem('admin_auth_token');
        const res = await fetch(`${API_URL}/payroll/employees/${selectedEmployee?.id}/ssn`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ password: ssnPassword }),
        });

        if (res.ok) {
          const data = await res.json();
          setRevealedSSN(data.ssn);
        } else {
          // Demo: show a fake full SSN
          setRevealedSSN(`123-45-${selectedEmployee?.ssn_last4}`);
        }
      } catch {
        // Demo fallback
        setRevealedSSN(`123-45-${selectedEmployee?.ssn_last4}`);
      }
      setSSNError('');
    } else {
      setSSNError('Invalid password. Access denied.');
      setRevealedSSN(null);
    }
  };

  const handleSaveTaxInfo = async () => {
    if (!editingTaxInfo) return;

    try {
      const token = localStorage.getItem('admin_auth_token');
      const payload: any = { ...editingTaxInfo };

      // Only include SSN if newly entered
      if (newSSN && newSSN.replace(/-/g, '').length === 9) {
        payload.ssn = newSSN;
      }

      const res = await fetch(`${API_URL}/payroll/employees/${editingTaxInfo.id}/tax-info`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        loadData();
      }
    } catch (error) {
      console.error('Failed to save tax info:', error);
    }

    // Update locally for demo
    setEmployees(prev => prev.map(e =>
      e.id === editingTaxInfo.id ? editingTaxInfo : e
    ));
    setEditingTaxInfo(null);
    setNewSSN('');
  };

  const handlePrintW2 = () => {
    window.print();
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-500">Loading payroll reports...</div>;
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Payroll Reports</h1>
        <p className="text-gray-500">W-2 forms, quarterly reports, and tax documents</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-4 overflow-x-auto">
          {[
            { id: 'w2', label: 'W-2 Forms' },
            { id: '1099', label: '1099-NEC Forms' },
            { id: 'employee-tax', label: 'Worker Tax Info' },
            { id: 'quarterly', label: 'Quarterly Reports' },
            { id: 'annual', label: 'Annual Reports' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* W-2 Forms Tab */}
      {activeTab === 'w2' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-gray-700">Tax Year:</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="border border-gray-300 rounded-lg px-3 py-2"
              >
                {[2026, 2025, 2024, 2023].map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              Generate All W-2s
            </button>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Employee</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">SSN</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">YTD Wages</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Federal Tax</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">SS Tax</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Medicare</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {employees.filter(e => !e.classification || e.classification.startsWith('w2')).map((emp) => (
                  <tr key={emp.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{emp.name}</div>
                      <div className="text-sm text-gray-500">{emp.email}</div>
                      <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">
                        {emp.classification === 'w2_salary' ? 'W-2 SALARY' : 'W-2 HOURLY'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-600 font-mono">{emp.ssn_masked}</span>
                        <button
                          onClick={() => handleViewSSN(emp)}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          View
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 font-medium">{formatCurrency(emp.ytd_gross)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatCurrency(emp.ytd_federal_tax)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatCurrency(emp.ytd_social_security)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatCurrency(emp.ytd_medicare)}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleViewW2(emp)}
                        className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                      >
                        View W-2
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 1099-NEC Forms Tab */}
      {activeTab === '1099' && (
        <div className="space-y-4">
          {/* 1099 Info Banner */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <h3 className="font-semibold text-orange-800 mb-2">1099-NEC Requirements</h3>
            <ul className="text-sm text-orange-700 space-y-1">
              <li>• Issue 1099-NEC to contractors who received $600 or more in nonemployee compensation</li>
              <li>• Deadline: January 31 to provide to recipients AND file with IRS</li>
              <li>• Includes payments for services, commissions, fees, and other compensation to non-employees</li>
              <li>• Texas does not require state copies (no state income tax)</li>
            </ul>
          </div>

          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-gray-700">Tax Year:</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="border border-gray-300 rounded-lg px-3 py-2"
              >
                {[2026, 2025, 2024, 2023].map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <button className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700">
              Generate All 1099-NECs
            </button>
          </div>

          {/* 1099 Recipients Table */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900">1099-NEC Recipients</h2>
              <p className="text-sm text-gray-500">Independent contractors paid $600+ during the tax year</p>
            </div>
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Contractor</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Tax ID</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Address</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Box 1: Nonemployee Comp</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Status</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {employees.filter(e => e.classification?.startsWith('1099') && (e.ytd_nonemployee_comp || e.ytd_gross) >= 600).map((emp) => (
                  <tr key={emp.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{emp.name}</div>
                      <div className="text-sm text-gray-500">{emp.email}</div>
                      <span className="text-xs px-2 py-0.5 bg-orange-100 text-orange-700 rounded">
                        {emp.classification?.replace('_', ' ').replace('1099', '1099-NEC').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-500">{emp.ein_or_ssn_type === 'ein' ? 'EIN' : 'SSN'}</div>
                      <span className="font-mono text-gray-600">{emp.ssn_masked}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {emp.address.street}<br />
                      {emp.address.city}, {emp.address.state} {emp.address.zip}
                    </td>
                    <td className="px-4 py-3 text-lg font-bold text-gray-900">
                      {formatCurrency(emp.ytd_nonemployee_comp || emp.ytd_gross)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs font-medium">
                        Pending
                      </span>
                    </td>
                    <td className="px-4 py-3 space-x-2">
                      <button
                        onClick={() => {
                          setSelectedEmployee(emp);
                          setShowW2Modal(true); // Reuse modal for 1099 preview
                        }}
                        className="text-orange-600 hover:text-orange-800 font-medium text-sm"
                      >
                        View 1099
                      </button>
                    </td>
                  </tr>
                ))}
                {employees.filter(e => e.classification?.startsWith('1099')).length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      No 1099 contractors found. Add contractors in Payroll &gt; Employee Setup.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* 1099 Summary */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">1099-NEC Summary - {selectedYear}</h3>
            <div className="grid grid-cols-3 gap-6">
              <div>
                <div className="text-sm text-gray-500">Total 1099 Recipients</div>
                <div className="text-2xl font-bold text-gray-900">
                  {employees.filter(e => e.classification?.startsWith('1099') && (e.ytd_nonemployee_comp || e.ytd_gross) >= 600).length}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Total Nonemployee Compensation</div>
                <div className="text-2xl font-bold text-orange-600">
                  {formatCurrency(employees.filter(e => e.classification?.startsWith('1099')).reduce((sum, e) => sum + (e.ytd_nonemployee_comp || e.ytd_gross), 0))}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Filing Deadline</div>
                <div className="text-lg font-bold text-gray-900">January 31, {selectedYear + 1}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Employee Tax Info Tab */}
      {activeTab === 'employee-tax' && (
        <div className="space-y-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-yellow-800">
              <strong>Security Notice:</strong> Social Security Numbers are encrypted and require admin password verification to view.
              Only the last 4 digits are displayed by default.
            </p>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900">Employee & Contractor Tax Information</h2>
              <p className="text-sm text-gray-500">Address and Tax ID information for W-2/1099 generation</p>
            </div>
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Worker</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Type</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Tax ID</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Address</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Filing Status</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {employees.map((emp) => (
                  <tr key={emp.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{emp.name}</div>
                      <div className="text-sm text-gray-500">{emp.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-gray-600">{emp.ssn_masked}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {emp.address.street}<br />
                      {emp.address.city}, {emp.address.state} {emp.address.zip}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 capitalize">
                      {emp.filing_status.replace('_', ' ')}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => {
                          setEditingTaxInfo(emp);
                          setNewSSN('');
                        }}
                        className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Quarterly Reports Tab */}
      {activeTab === 'quarterly' && (
        <div className="space-y-4">
          <div className="flex items-center gap-4 mb-4">
            <label className="text-sm font-medium text-gray-700">Year:</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="border border-gray-300 rounded-lg px-3 py-2"
            >
              {[2026, 2025, 2024].map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { quarter: 'Q1', months: 'Jan - Mar', status: 'Filed', dueDate: 'Apr 30' },
              { quarter: 'Q2', months: 'Apr - Jun', status: 'Filed', dueDate: 'Jul 31' },
              { quarter: 'Q3', months: 'Jul - Sep', status: 'Filed', dueDate: 'Oct 31' },
              { quarter: 'Q4', months: 'Oct - Dec', status: 'Pending', dueDate: 'Jan 31' },
            ].map((q) => (
              <div key={q.quarter} className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-semibold text-gray-900">{q.quarter} {selectedYear}</h3>
                    <p className="text-sm text-gray-500">{q.months}</p>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    q.status === 'Filed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {q.status}
                  </span>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <div>
                      <div className="font-medium text-gray-900">Form 941</div>
                      <div className="text-xs text-gray-500">Employer's Quarterly Federal Tax Return</div>
                    </div>
                    <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                      {q.status === 'Filed' ? 'View' : 'Generate'}
                    </button>
                  </div>

                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <div>
                      <div className="font-medium text-gray-900">Form 940</div>
                      <div className="text-xs text-gray-500">FUTA Tax Return (Annual, Q4 only)</div>
                    </div>
                    {q.quarter === 'Q4' && (
                      <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                        {q.status === 'Filed' ? 'View' : 'Generate'}
                      </button>
                    )}
                  </div>

                  <div className="text-xs text-gray-500 mt-2">
                    Due Date: {q.dueDate}, {selectedYear + (q.quarter === 'Q4' ? 1 : 0)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Annual Reports Tab */}
      {activeTab === 'annual' && (
        <div className="space-y-4">
          <div className="flex items-center gap-4 mb-4">
            <label className="text-sm font-medium text-gray-700">Tax Year:</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="border border-gray-300 rounded-lg px-3 py-2"
            >
              {[2025, 2024, 2023].map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="font-semibold text-gray-900 mb-2">W-2 Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Employees</span>
                  <span className="font-medium">{employees.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Wages</span>
                  <span className="font-medium">{formatCurrency(employees.reduce((sum, e) => sum + e.ytd_gross, 0))}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Federal Tax</span>
                  <span className="font-medium">{formatCurrency(employees.reduce((sum, e) => sum + e.ytd_federal_tax, 0))}</span>
                </div>
              </div>
              <button className="w-full mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
                Generate W-2s
              </button>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="font-semibold text-gray-900 mb-2">W-3 Transmittal</h3>
              <p className="text-sm text-gray-500 mb-4">Transmittal of Wage and Tax Statements</p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total SS Wages</span>
                  <span className="font-medium">{formatCurrency(employees.reduce((sum, e) => sum + Math.min(e.ytd_gross, 168600), 0))}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total SS Tax</span>
                  <span className="font-medium">{formatCurrency(employees.reduce((sum, e) => sum + e.ytd_social_security, 0))}</span>
                </div>
              </div>
              <button className="w-full mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm">
                Generate W-3
              </button>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="font-semibold text-gray-900 mb-2">1099 Forms</h3>
              <p className="text-sm text-gray-500 mb-4">For contractors and vendors</p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">1099-NEC Recipients</span>
                  <span className="font-medium">{employees.filter(e => e.classification?.startsWith('1099') && (e.ytd_nonemployee_comp || e.ytd_gross) >= 600).length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total 1099 Compensation</span>
                  <span className="font-medium text-orange-600">{formatCurrency(employees.filter(e => e.classification?.startsWith('1099')).reduce((sum, e) => sum + (e.ytd_nonemployee_comp || e.ytd_gross), 0))}</span>
                </div>
              </div>
              <button
                onClick={() => setActiveTab('1099')}
                className="w-full mt-4 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm"
              >
                Generate 1099-NECs
              </button>
            </div>
          </div>

          {/* Tax Summary Card */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Annual Tax Summary - {selectedYear}</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div>
                <div className="text-sm text-gray-500">Total Gross Wages</div>
                <div className="text-2xl font-bold text-gray-900">
                  {formatCurrency(employees.reduce((sum, e) => sum + e.ytd_gross, 0))}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Total Federal Tax</div>
                <div className="text-2xl font-bold text-red-600">
                  {formatCurrency(employees.reduce((sum, e) => sum + e.ytd_federal_tax, 0))}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Total FICA (SS + Medicare)</div>
                <div className="text-2xl font-bold text-orange-600">
                  {formatCurrency(employees.reduce((sum, e) => sum + e.ytd_social_security + e.ytd_medicare, 0) * 2)}
                </div>
                <div className="text-xs text-gray-400">Employee + Employer share</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">State Tax (TX)</div>
                <div className="text-2xl font-bold text-green-600">$0.00</div>
                <div className="text-xs text-gray-400">Texas has no state income tax</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* W-2 Preview Modal */}
      {showW2Modal && selectedEmployee && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">W-2 Preview - {selectedYear}</h2>
                <button
                  onClick={() => setShowW2Modal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* W-2 Form Preview */}
              <div className="border-2 border-gray-300 p-6 bg-gray-50 font-mono text-sm">
                <div className="text-center mb-4">
                  <div className="text-lg font-bold">Form W-2 Wage and Tax Statement</div>
                  <div className="text-gray-600">{selectedYear}</div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  {/* Employer Info */}
                  <div className="border border-gray-300 p-3 bg-white">
                    <div className="text-xs text-gray-500 mb-1">a. Employee's social security number</div>
                    <div className="font-bold">{selectedEmployee.ssn_masked}</div>
                  </div>
                  <div className="border border-gray-300 p-3 bg-white">
                    <div className="text-xs text-gray-500 mb-1">b. Employer identification number (EIN)</div>
                    <div className="font-bold">{employerInfo.ein}</div>
                  </div>
                </div>

                <div className="border border-gray-300 p-3 bg-white mb-4">
                  <div className="text-xs text-gray-500 mb-1">c. Employer's name, address, and ZIP code</div>
                  <div className="font-bold">{employerInfo.name}</div>
                  <div>{employerInfo.address}</div>
                </div>

                <div className="border border-gray-300 p-3 bg-white mb-4">
                  <div className="text-xs text-gray-500 mb-1">e. Employee's name and address</div>
                  <div className="font-bold">{selectedEmployee.name}</div>
                  <div>{selectedEmployee.address.street}</div>
                  <div>{selectedEmployee.address.city}, {selectedEmployee.address.state} {selectedEmployee.address.zip}</div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="border border-gray-300 p-3 bg-white">
                    <div className="text-xs text-gray-500 mb-1">1. Wages, tips, other compensation</div>
                    <div className="font-bold text-lg">{formatCurrency(selectedEmployee.ytd_gross)}</div>
                  </div>
                  <div className="border border-gray-300 p-3 bg-white">
                    <div className="text-xs text-gray-500 mb-1">2. Federal income tax withheld</div>
                    <div className="font-bold text-lg">{formatCurrency(selectedEmployee.ytd_federal_tax)}</div>
                  </div>
                  <div className="border border-gray-300 p-3 bg-white">
                    <div className="text-xs text-gray-500 mb-1">3. Social security wages</div>
                    <div className="font-bold">{formatCurrency(Math.min(selectedEmployee.ytd_gross, 168600))}</div>
                  </div>
                  <div className="border border-gray-300 p-3 bg-white">
                    <div className="text-xs text-gray-500 mb-1">4. Social security tax withheld</div>
                    <div className="font-bold">{formatCurrency(selectedEmployee.ytd_social_security)}</div>
                  </div>
                  <div className="border border-gray-300 p-3 bg-white">
                    <div className="text-xs text-gray-500 mb-1">5. Medicare wages and tips</div>
                    <div className="font-bold">{formatCurrency(selectedEmployee.ytd_gross)}</div>
                  </div>
                  <div className="border border-gray-300 p-3 bg-white">
                    <div className="text-xs text-gray-500 mb-1">6. Medicare tax withheld</div>
                    <div className="font-bold">{formatCurrency(selectedEmployee.ytd_medicare)}</div>
                  </div>
                  <div className="border border-gray-300 p-3 bg-white">
                    <div className="text-xs text-gray-500 mb-1">15. State | 16. State wages</div>
                    <div className="font-bold">TX | {formatCurrency(selectedEmployee.ytd_gross)}</div>
                  </div>
                  <div className="border border-gray-300 p-3 bg-white">
                    <div className="text-xs text-gray-500 mb-1">17. State income tax</div>
                    <div className="font-bold">$0.00</div>
                    <div className="text-xs text-gray-400">TX has no state income tax</div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowW2Modal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Close
                </button>
                <button
                  onClick={handlePrintW2}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Print W-2
                </button>
                <button className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                  Download PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SSN Verification Modal */}
      {showSSNModal && selectedEmployee && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">View Full SSN</h2>
              <button
                onClick={() => {
                  setShowSSNModal(false);
                  setRevealedSSN(null);
                  setSSNPassword('');
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600">Employee: <strong>{selectedEmployee.name}</strong></p>
              <p className="text-sm text-gray-600">Current Display: <span className="font-mono">{selectedEmployee.ssn_masked}</span></p>
            </div>

            {!revealedSSN ? (
              <>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-yellow-800">
                    <strong>Security:</strong> Enter your admin password to view the full Social Security Number.
                  </p>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Admin Password</label>
                  <input
                    type="password"
                    value={ssnPassword}
                    onChange={(e) => setSSNPassword(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleVerifySSNPassword()}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    placeholder="Enter admin password"
                    autoFocus
                  />
                  {ssnError && (
                    <p className="text-red-600 text-sm mt-1">{ssnError}</p>
                  )}
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowSSNModal(false);
                      setSSNPassword('');
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleVerifySSNPassword}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Verify & View
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-green-800 mb-2">Full SSN (auto-hides in 30 seconds):</p>
                  <p className="text-2xl font-mono font-bold text-green-900">{revealedSSN}</p>
                </div>

                <button
                  onClick={() => {
                    setShowSSNModal(false);
                    setRevealedSSN(null);
                    setSSNPassword('');
                  }}
                  className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  Close
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Edit Tax Info Modal */}
      {editingTaxInfo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Edit Tax Information - {editingTaxInfo.name}
            </h2>

            <div className="space-y-4">
              {/* SSN Section */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Social Security Number
                </label>
                <p className="text-sm text-gray-500 mb-2">
                  Current: {editingTaxInfo.ssn_masked} (encrypted)
                </p>
                <input
                  type="password"
                  value={newSSN}
                  onChange={(e) => {
                    // Format SSN as user types
                    let value = e.target.value.replace(/\D/g, '');
                    if (value.length > 9) value = value.slice(0, 9);
                    if (value.length > 5) {
                      value = `${value.slice(0, 3)}-${value.slice(3, 5)}-${value.slice(5)}`;
                    } else if (value.length > 3) {
                      value = `${value.slice(0, 3)}-${value.slice(3)}`;
                    }
                    setNewSSN(value);
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 font-mono"
                  placeholder="Enter new SSN (###-##-####)"
                  maxLength={11}
                />
                <p className="text-xs text-gray-400 mt-1">
                  Leave blank to keep current SSN. Will be encrypted before storage.
                </p>
              </div>

              {/* Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                <input
                  type="text"
                  value={editingTaxInfo.address.street}
                  onChange={(e) => setEditingTaxInfo({
                    ...editingTaxInfo,
                    address: { ...editingTaxInfo.address, street: e.target.value }
                  })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input
                    type="text"
                    value={editingTaxInfo.address.city}
                    onChange={(e) => setEditingTaxInfo({
                      ...editingTaxInfo,
                      address: { ...editingTaxInfo.address, city: e.target.value }
                    })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <input
                    type="text"
                    value={editingTaxInfo.address.state}
                    onChange={(e) => setEditingTaxInfo({
                      ...editingTaxInfo,
                      address: { ...editingTaxInfo.address, state: e.target.value.toUpperCase() }
                    })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    maxLength={2}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ZIP</label>
                  <input
                    type="text"
                    value={editingTaxInfo.address.zip}
                    onChange={(e) => setEditingTaxInfo({
                      ...editingTaxInfo,
                      address: { ...editingTaxInfo.address, zip: e.target.value }
                    })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    maxLength={10}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Filing Status</label>
                <select
                  value={editingTaxInfo.filing_status}
                  onChange={(e) => setEditingTaxInfo({
                    ...editingTaxInfo,
                    filing_status: e.target.value as any
                  })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="single">Single</option>
                  <option value="married">Married Filing Jointly</option>
                  <option value="head_of_household">Head of Household</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setEditingTaxInfo(null);
                  setNewSSN('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTaxInfo}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PayrollReportsPage;
